/**
 * TopHeader — thin translucent bar spanning viewport width.
 * Left: SceneSelector. Center: "Previs Pro Timeline" branding. Right: review status + comment toggle.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { isLaptopSplitActive } from '@/platform/laptopSplitState.ts';
import { capabilities } from '@/capabilities.ts';
import { APPROVAL_COLORS } from '@/export/playerConstants.ts';
import type { ApprovalStatus } from '@/types/timeline.ts';
import { SceneSelector } from './SceneSelector.tsx';
import styles from './TopHeader.module.css';

const APPROVAL_OPTIONS: { value: ApprovalStatus; labelKey: string; color: string }[] = [
  { value: 'pending', labelKey: 'approvalPending', color: APPROVAL_COLORS.pending },
  { value: 'approved', labelKey: 'approvalApproved', color: APPROVAL_COLORS.approved },
  { value: 'notes', labelKey: 'approvalNotes', color: APPROVAL_COLORS.notes },
];

export function TopHeader() {
  const { t } = useTranslation('topHeader');
  const project = useProjectStore(s => s.project);
  const readOnly = useProjectStore(s => s.readOnly);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const allApprovals = useCommentStore(s => s.approvals);
  const setApproval = useCommentStore(s => s.setApproval);
  const allComments = useCommentStore(s => s.comments);
  const commentPanelOpen = useUiStore(s => s.commentPanelOpen);
  const aiWorkspaceOpen = useUiStore(s => s.aiWorkspaceOpen);
  const audioWorkspaceOpen = useUiStore(s => s.audioWorkspaceOpen);
  const scriptPanelOpen = useUiStore(s => s.scriptPanelOpen);
  const toggleScriptPanel = useUiStore(s => s.toggleScriptPanel);
  const applyScriptPanelAutoOpen = useUiStore(s => s.applyScriptPanelAutoOpen);
  const selectShot = useProjectStore(s => s.selectShot);
  const selectedShotId = useProjectStore(s => s.selectedShotId);
  const sessionRevision = useProjectStore(s => s.sessionRevision);

  const shot = getShotAtTime(currentTime);
  const isMobile = useMobileLayout();

  // Auto-open script panel only when current scene has real screenplay content and not on mobile
  // Skip in online review mode — reviewers get script closed by default
  const activeSceneId = useProjectStore(s => s.activeSceneId);
  const sessionId = useProjectStore(s => s.sessionId);
  const currentScene = useMemo(() => {
    if (!project) return null;
    if (activeSceneId !== null) return project.scenes.find(s => s.id === activeSceneId) ?? null;
    return project.scenes.find(s => !s.isOmitted) ?? null;
  }, [project, activeSceneId]);

  // 20260920 (owner) — this is a SUGGESTION now, not a decision. applyScriptPanelAutoOpen is a
  // no-op once the user has worked the panel themselves, because this component unmounts with the
  // Timeline tab and the effect below therefore re-runs on every return to it: before the split it
  // re-applied the auto-open there and put back a panel the user had just closed. The store holds
  // the flag (scriptPanelDecidedByUser) and does the gating — nothing here reads it, deliberately,
  // since a dependency on it would re-run this effect the instant the user closed the panel.
  //
  // Both early-outs are unchanged and still come FIRST, so neither mode can be talked into an open
  // panel by the new flag: mobile and online review keep the script closed by never suggesting at
  // all, exactly as before.
  //
  // 20260929 - AND THE LAPTOP SPLIT, for the phone's reason: there the script panel is the fullscreen
  // overlay too (ScriptPanel.tsx), so a suggestion would cover the whole window on the way into the tab.
  // Read imperatively, NOT a dependency: SceneBoardPage publishes it from a layout effect, which has run
  // before this passive effect even on the tab's first mount; and a dependency would re-run this effect
  // when the split collapses under the Audio workspace and put back a panel that then goes fullscreen
  // the moment the workspace closes. A panel the user already had open stays open (and fullscreen).
  useEffect(() => {
    if (!currentScene || isMobile || sessionId || isLaptopSplitActive()) return;
    const sp = currentScene.screenplay;
    const hasRealContent = !!currentScene.screenplayText?.trim()
      || (sp && ((sp.dialogue?.length ?? 0) > 0 || (sp.actions?.length ?? 0) > 0 || (sp.transitions?.length ?? 0) > 0));
    applyScriptPanelAutoOpen(!!hasRealContent);
  }, [currentScene?.id, isMobile, sessionId, applyScriptPanelAutoOpen]);

  // ── Right: review status ──────────────────────────────────────────
  const approval = useMemo(() => {
    if (!shot) return null;
    const shotApprovals = allApprovals.filter(a => a.shotId === shot.id);
    if (shotApprovals.length === 0) return null;
    return shotApprovals.reduce((latest, a) => a.revision > latest.revision ? a : latest);
  }, [allApprovals, shot]);

  const commentCount = useMemo(() =>
    shot ? allComments.filter(c => c.shotId === shot.id && !c.parentCommentId).length : 0,
    [allComments, shot],
  );

  const status = approval?.status ?? 'pending';

  // Comment button strobe — pulse when entering a shot with comments + not approved
  const [commentStrobe, setCommentStrobe] = useState(false);
  const prevStrobeShotRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shot) return;
    if (shot.id === prevStrobeShotRef.current) return;
    prevStrobeShotRef.current = shot.id;

    const hasComments = allComments.some(c => c.shotId === shot.id);
    if (hasComments && status !== 'approved' && !commentPanelOpen) {
      setCommentStrobe(true);
      const timer = setTimeout(() => setCommentStrobe(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setCommentStrobe(false);
    }
  }, [shot?.id, allComments, status, commentPanelOpen]);

  // Incoming remote comment flash — 3x bright blink like a transmission light
  const [commentIncoming, setCommentIncoming] = useState(false);
  const remoteCommentPing = useUiStore(s => s.remoteCommentPing);
  const prevPingRef = useRef(remoteCommentPing);

  useEffect(() => {
    if (remoteCommentPing === prevPingRef.current) return;
    prevPingRef.current = remoteCommentPing;
    if (commentPanelOpen) return; // Already looking at comments — no need to flash
    setCommentIncoming(true);
    const timer = setTimeout(() => setCommentIncoming(false), 1200);
    return () => clearTimeout(timer);
  }, [remoteCommentPing, commentPanelOpen]);

  const handleCommentToggle = useCallback(() => {
    const ps = useProjectStore.getState();
    const pb = usePlaybackStore.getState();
    // Auto-select current shot so the panel has something to show
    if (ps.selectedShotId === null) {
      const s = ps.getShotAtTime(pb.currentTime);
      if (s) ps.selectShot(s.id);
    }
    useUiStore.getState().toggleCommentPanel();
  }, []);

  // ── Approval popover state ──────────────────────────────────────
  const [approvalPopoverOpen, setApprovalPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);

  const handlePillClick = useCallback(() => {
    setApprovalPopoverOpen(prev => !prev);
  }, []);

  const handleApprovalSelect = useCallback((value: ApprovalStatus) => {
    if (shot) {
      if (selectedShotId !== shot.id) selectShot(shot.id);
      setApproval(shot.id, value);
    }
    setApprovalPopoverOpen(false);
  }, [shot, selectedShotId, selectShot, setApproval]);

  // Close on outside click
  useEffect(() => {
    if (!approvalPopoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        pillRef.current && !pillRef.current.contains(e.target as Node)
      ) {
        setApprovalPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [approvalPopoverOpen]);

  // Close on Escape
  useEffect(() => {
    if (!approvalPopoverOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setApprovalPopoverOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [approvalPopoverOpen]);

  // Close on shot change
  useEffect(() => {
    setApprovalPopoverOpen(false);
  }, [shot?.id]);

  return (
    <div className={`${styles.header} ${readOnly ? styles.headerReview : ''}`}>
      {/* Left: Script toggle + Scene Selector */}
      <div className={styles.left} data-orient="scene-selector">
        {project && (
          <button
            className={`${styles.scriptBtn} ${scriptPanelOpen ? styles.scriptBtnActive : ''}`}
            onClick={toggleScriptPanel}
            title={t('toggleScriptPanel')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </button>
        )}
        <SceneSelector />
      </div>

      {/* Center: branding / project name — changes based on active workspace */}
      <div className={styles.center}>
        <span className={styles.branding}>
          {aiWorkspaceOpen ? t('styleAndVideoGrading')
            : audioWorkspaceOpen ? t('previsAudioSystem')
            : sessionId && project?.title
              ? `${project.title}${sessionRevision ? ` v${sessionRevision}` : ''}`
            : capabilities.bridge && project?.title ? project.title
            : t('previsProTimeline')}
        </span>
      </div>

      {/* Right: Review status + comment toggle */}
      <div className={styles.right} data-orient="comments-approval">
        {capabilities.comments && (
          <>
            <div className={styles.approvalWrapper}>
              <span
                ref={pillRef}
                className={styles.statusPill}
                style={{ background: APPROVAL_COLORS[status] ?? '#888' }}
                onClick={handlePillClick}
                title={t('changeApprovalStatus')}
              >
                {status.replace('_', ' ')}
                <svg className={styles.pillArrow} width="10" height="6" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z" fill="currentColor" /></svg>
              </span>
              {approvalPopoverOpen && (
                <div ref={popoverRef} className={styles.approvalPopover}>
                  {APPROVAL_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`${styles.approvalOption} ${status === opt.value ? styles.approvalOptionActive : ''}`}
                      style={{
                        '--opt-color': opt.color,
                        background: status === opt.value ? opt.color : undefined,
                      } as React.CSSProperties}
                      onClick={() => handleApprovalSelect(opt.value)}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className={`${styles.commentBtn} ${commentPanelOpen ? styles.commentBtnActive : ''} ${commentIncoming ? styles.commentBtnIncoming : commentStrobe ? styles.commentBtnStrobe : ''}`}
              onClick={handleCommentToggle}
              title={t('toggleComments')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              {commentCount > 0 && (
                <span className={styles.commentBadge}>{commentCount}</span>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
